import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TreeStructure, ArrowLeft } from "@phosphor-icons/react";

const SECTIONS = [
  {
    q: "How do I fill out onboarding?",
    a: "After an admin approves your join request, open your circle and click 'Open onboarding' on the banner. Pick your family branch (paternal, maternal, or both), type the names of your parents, siblings, spouse and children, and submit. We auto-match names already in the circle — verified relatives appear instantly on your map.",
  },
  {
    q: "How do I add a placeholder family member?",
    a: "Open Circle Settings (gear icon) → Placeholders. Click 'Add a placeholder seat,' type the person's name, pick their relationship from the dropdown (Father, Mother, Son, Daughter, Brother, Sister, Spouse, Cousin, Grandfather, Grandmother, Uncle, Aunt, Nephew, Niece, Grandchild, Great-Grandfather, Great-Grandmother, Great-Grandchild) and choose who they connect to (a circle member or another placeholder). Save — a dashed amber 'open seat' instantly appears on the family tree in the correct generation.",
  },
  {
    q: "How do I claim a placeholder spot?",
    a: "If you've just been invited and there's already a placeholder for you on the tree, look for the 'Find yourself on the family tree' amber banner above the tree. Click your name on the dashed amber seat that matches you — a dialog asks 'Is this you — [Name]?' Confirm and the seat turns solid with your photo. Every relationship drawn through the placeholder transfers onto your profile, and your generation position is locked in.",
  },
  {
    q: "How does the 'Connected to' field work?",
    a: "When you add a placeholder you pick a real circle member or another placeholder as their connection point. The tree uses your relationship choice plus the connected person's position to place the placeholder in the right generation — e.g. a 'Son' connected to your Mother sits one row below her, which is the same row as you (your brother).",
  },
  {
    q: "How do I expand or collapse branches on the family tree?",
    a: "On the Map tab, switch to the 'Tree' layout. Every parent node shows a small −/+ chip just below the photo. Click − to collapse that person's subtree (their descendants disappear), click + to expand again. Drag to pan the canvas and scroll to zoom — perfect for navigating large families without clutter.",
  },
  {
    q: "How do I tell verified members from unverified placeholders?",
    a: "Verified members have a solid ring around their photo (gold for paternal side, deep green for maternal, sage for both/unset). Unverified placeholders use a dashed amber ring with a 'Not yet joined' pill above them — and the lines connecting them to the rest of the family are amber-dashed too. Once a placeholder is claimed it converts into a verified solid node.",
  },
  {
    q: "How do deceased members appear differently on the tree?",
    a: "Memorial profiles use a dashed dark-brown ring, a muted cream fill so they sit visually back from living relatives, and a tiny gold candle flame icon in the top-right corner. Their birth–death years appear in a small pill above the photo. The relationship label still shows their kinship to you (e.g. 'In memoriam · Grandparent').",
  },
  {
    q: "How do relationships get verified?",
    a: "Whenever someone adds a relationship — say, 'X is my mother' — the other party (and any admin) can confirm it. Two confirmations or one admin confirmation lock the claim in. A verified relationship is what makes the map calculate cousins, aunts, etc. and is also what promotes auto-approved members onto the visible map. When you claim a placeholder, a verified relationship is materialised automatically so your tree position survives.",
  },
  {
    q: "What does a Branch Leader do?",
    a: "Branch Leaders are trusted members an admin appoints to oversee one side of the family — paternal or maternal. They can approve new members joining their branch and verify relationships on their side, taking some of the moderation burden off the owner.",
  },
  {
    q: "How do I invite someone?",
    a: "Inside your circle, open the Members tab → 'Invite a relative.' We mint a 30-day code that you can share as a link, text, or email. If you're adding a placeholder family member with an email address, an invite is sent automatically — and you can resend it any time from the Circle Settings → Placeholders tab.",
  },
  {
    q: "How do I edit my profile?",
    a: "Click your avatar in the top navigation. You can update your display name, photo and bio, and choose whether your email is visible to other circle members. Photo uploads auto-rotate (no more sideways selfies) and are compressed silently before upload.",
  },
  {
    q: "How do memorials work?",
    a: "Memorials honor relatives who've passed. Open the Memorials tab, click 'Add memorial,' fill in name, birth/death years, branch and bio. Five circle members confirm and the memorial appears on the map with the dashed dark ring, candle flame icon and the years inscribed above. Verified close kin (children, parents, siblings, then grandchildren) and the circle admin can edit the memorial later.",
  },
];

export default function Help() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#0E1E18] text-ivory antialiased selection:bg-gold/30 selection:text-ivory">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full bg-forest/40 blur-3xl" />
      </div>

      {/* Top bar */}
      <header className="max-w-5xl mx-auto px-5 md:px-10 py-6 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5" data-testid="help-logo">
          <span className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center border border-gold/30">
            <TreeStructure size={22} weight="duotone" />
          </span>
          <span className="font-display text-2xl tracking-tight">LegacyLink</span>
        </Link>
        <Link to={user ? "/dashboard" : "/"} className="inline-flex items-center gap-1.5 text-sm text-ivory/70 hover:text-ivory" data-testid="help-back-link">
          <ArrowLeft size={16} weight="bold" />
          Back
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-10 pt-8 pb-20" data-testid="help-page">
        <p className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-gold mb-4">Help & FAQ</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
          Everything you need to know
          <br />
          <span className="text-gold italic">about LegacyLink.</span>
        </h1>
        <p className="mt-6 text-ivory/65 max-w-2xl leading-relaxed">
          A short guide to onboarding, adding relatives, verifying connections, and keeping the family map alive.
        </p>

        <div className="mt-12 divide-y divide-white/8" data-testid="help-list">
          {SECTIONS.map((item, i) => (
            <details key={i} open={i === 0} className="group py-5 lg:py-6" data-testid={`help-item-${i}`}>
              <summary className="flex items-center justify-between gap-6 cursor-pointer list-none">
                <h3 className="font-display text-lg sm:text-xl lg:text-2xl text-ivory pr-4">{item.q}</h3>
                <span className="shrink-0 w-9 h-9 rounded-full border border-gold/40 text-gold flex items-center justify-center transition-transform group-open:rotate-45">
                  <span className="text-xl leading-none">+</span>
                </span>
              </summary>
              <p className="mt-4 text-ivory/65 leading-relaxed max-w-3xl whitespace-pre-line">{item.a}</p>
            </details>
          ))}
        </div>

        <div className="mt-16 rounded-2xl border border-gold/30 bg-gold/5 p-6 lg:p-8">
          <h3 className="font-display text-2xl text-ivory mb-2">Still stuck?</h3>
          <p className="text-ivory/70 leading-relaxed">
            Use the floating feedback button (right edge of any page) to send a note. Your circle admin reviews feedback in their admin view.
          </p>
        </div>
      </main>
    </div>
  );
}
